import {
  ENDPOINT_COTTON_PLANS,
  ENDPOINT_MONITORING_ACTIONS,
  ENDPOINT_MONITORING_DISTRICT,
  ENDPOINT_MONITORING_FARMER_ACTIONS,
  ENDPOINT_MONITORING_FARMER_AREA,
  ENDPOINT_MONITORING_FARMERS_PROJECT,
  ENDPOINT_MONITORING_PAYMENT,
  ENDPOINT_MONITORING_REPUBLIC,
  ENDPOINT_FARMER_ACTION_INSPECT,
  ENDPOINT_ACTIONS_BY_MONTH,
} from '@/common/constants';
import { HttpApi } from '@/common/http';
import { IPageable } from '@/common/models';
import { IParams } from '@/features/lands/models/ILands';
import { create } from 'zustand';
import {
  IAction,
  IActionListItem,
  IAreaData,
  ICottonData,
  IDataRepublic,
  IFarmer,
  IFarmerActions,
  TMonitoring,
} from '../models/IMonitoring';

const httpApi = new HttpApi();

export const useMonitoringStore = create<TMonitoring>((set) => ({
  areaData: {} as IAreaData,
  payment: [],
  dataRepublic: [],
  dataRepublicCachedAt: null,
  dataDistrict: [],
  dataDistrictCachedAt: null,
  cottonData: [],
  actionsData: [],
  farmerActions: {} as IFarmerActions,
  farmerData: {} as IPageable<IFarmer>,
  cottonDistrictData: [],
  areaLoading: false,
  cottonLoading: false,
  farmerLoading: false,
  paymentLoading: false,
  actionsLoading: false,
  districtLoading: false,
  republicLoading: false,
  farmerActionsLoading: false,
  cottonDistrictLoading: false,
  actionListData: [],
  actionListLoading: false,
  actionInspectData: null,
  actionInspectLoading: false,
  fetchActionList: async (params: {
    plant_id: string;
    region_id: string;
    month: string;
    year: string;
  }) => {
    set({ actionListLoading: true });
    const res = await httpApi.get<IActionListItem[]>(
      ENDPOINT_ACTIONS_BY_MONTH,
      params,
    );
    set({ actionListData: res || [] });
    setTimeout(() => {
      set({ actionListLoading: false });
    }, 300);
  },
  fetchMonitoringRepublic: async (plant_id: string, year: string) => {
    set({ republicLoading: true });
    const res = await httpApi.get<{ cachedAt: string, data: IDataRepublic[] }>(
      ENDPOINT_MONITORING_REPUBLIC,
      { plant_id, year },
    );
    set({ dataRepublic: res?.data || [], dataRepublicCachedAt: res?.cachedAt || null });
    setTimeout(() => {
      set({ republicLoading: false });
    }, 300);
  },
  fetchMonitoringDistrict: async (params: {
    region_id: string;
    year: string;
    plant_id: string;
  }) => {
    set({ districtLoading: true });
    const res = await httpApi.get<{ cachedAt: string, data: IDataRepublic[] }>(
      ENDPOINT_MONITORING_DISTRICT + `/${params.region_id}`,
      { year: params.year, plant_id: params.plant_id },
    );
    set({ dataDistrict: res?.data || [], dataDistrictCachedAt: res?.cachedAt || null });
    setTimeout(() => {
      set({ districtLoading: false });
    }, 300);
  },
  fetchCottonRepublic: async (plant_id: string, year: string) => {
    set({ cottonLoading: true });
    const res = await httpApi.get<ICottonData[]>(ENDPOINT_COTTON_PLANS, { plant_id, year });
    set({ cottonData: res });
    setTimeout(() => {
      set({ cottonLoading: false });
    }, 300);
  },
  fetchCottonDistrict: async (params: { region_id?: number, year: string, plant_id: string }) => {
    set({ cottonDistrictLoading: true });
    const res = await httpApi.get<ICottonData[]>(
      ENDPOINT_COTTON_PLANS + `/${params.region_id}`,
      { year: params.year, plant_id: params.plant_id },
    );
    set({ cottonDistrictData: res });
    setTimeout(() => {
      set({ cottonDistrictLoading: false });
    }, 300);
  },
  fetchMonitoringFarmers: async (params: IParams) => {
    set({ farmerLoading: true });
    const res = await httpApi.get<IPageable<IFarmer>>(
      ENDPOINT_MONITORING_FARMERS_PROJECT,
      params.query,
    );
    set({ farmerData: res });
    setTimeout(() => {
      set({ farmerLoading: false });
    }, 300);
  },
  fetchMonitoringActions: async (params: {
    month: string;
    type: string;
    id: string | undefined;
  }) => {
    set({ actionsLoading: true });
    const res = await httpApi.get<IAction[]>(
      ENDPOINT_MONITORING_ACTIONS +
      `/${params?.month}/${params?.type}/${params.id}`,
      {},
    );
    set({ actionsData: res });
    setTimeout(() => {
      set({ actionsLoading: false });
    }, 300);
  },
  fetchFarmersArea: async (params: {
    district_id: string | undefined;
    project_id: string | undefined;
    plant_id: string;
  }) => {
    set({ areaLoading: true });
    const res = await httpApi.get<IAreaData>(
      ENDPOINT_MONITORING_FARMER_AREA,
      params,
    );
    set({ areaData: res });
    setTimeout(() => {
      set({ areaLoading: false });
    }, 300);
  },
  fetchFarmersActions: async (params: { id: string }) => {
    set({ farmerActionsLoading: true });
    const res = await httpApi.get<IFarmerActions>(
      ENDPOINT_MONITORING_FARMER_ACTIONS + `/${params.id}`,
      {},
    );
    set({ farmerActions: res });
    setTimeout(() => {
      set({ farmerActionsLoading: false });
    }, 300);
  },
  fetchMonitoringPayment: async (params: {
    district_id: string | undefined;
    farmer_action_by_region_id: string | undefined;
  }) => {
    set({ paymentLoading: true });
    const res = await httpApi.get(ENDPOINT_MONITORING_PAYMENT, params);
    set({ payment: res });
    setTimeout(() => {
      set({ paymentLoading: false });
    }, 300);
  },
  fetchActionInspect: async (params: {
    farmer_action_by_project_id: string;
    farmer_action_by_project_contour_id: string | null;
  }) => {
    set({ actionInspectLoading: true });
    try {
      const queryParams: Record<string, string> = {};
      if (params.farmer_action_by_project_contour_id) {
        queryParams.farmer_action_by_project_contour_id =
          params.farmer_action_by_project_contour_id;
      }

      const res = await httpApi.get<any>(
        `${ENDPOINT_FARMER_ACTION_INSPECT}/${params.farmer_action_by_project_id}`,
        queryParams,
      );
      set({ actionInspectData: res || null });
    } catch (error) {
      console.error('Error fetching action监测 data:', error);
      set({ actionInspectData: null });
    } finally {
      setTimeout(() => {
        set({ actionInspectLoading: false });
      }, 300);
    }
  },
  clearActionInspectData: () => {
    set({ actionInspectData: null, actionInspectLoading: false });
  },
}));
